import { CSV_CONCURRENCY } from './csvExport';

type BatchedLookupOptions<V> = {
  /** The endpoint's limit on ids a request. */
  batchSize: number;
  /** One request's ids to `[id, value]` pairs; an id it leaves out is `missing`. */
  fetchBatch: (
    ids: string[],
    signal: AbortSignal,
  ) => Promise<Iterable<readonly [string, V]>>;
  /** What an id the API doesn't return maps to, so it isn't asked for again. */
  missing: V;
};

/**
 * Values for an export's rows by id, `batchSize` ids a request, asking only for
 * ids it hasn't seen or isn't already asking for. Make one per export: its
 * cache never expires, so one reused across exports would write stale values
 * (a renamed client's old name, say).
 */
export const createBatchedLookup = <V>({
  batchSize,
  fetchBatch,
  missing,
}: BatchedLookupOptions<V>) => {
  const values = new Map<string, V>();
  // The export runs its pages side by side, and pages usually share ids: a
  // page waits on the other's request for an id rather than asking again.
  const inFlight = new Map<string, Promise<void>>();

  const fetchValues = async (ids: string[], signal: AbortSignal) => {
    const batches = Array.from(
      { length: Math.ceil(ids.length / batchSize) },
      (_, index) => ids.slice(index * batchSize, (index + 1) * batchSize),
    );
    const responses = [];

    // A few at a time, not in turn and not all at once: the export's next page
    // waits on these, but a page with many ids must not burst past the
    // export's request budget and get throttled.
    for (let start = 0; start < batches.length; start += CSV_CONCURRENCY) {
      responses.push(
        ...(await Promise.all(
          batches
            .slice(start, start + CSV_CONCURRENCY)
            .map((batch) => fetchBatch(batch, signal)),
        )),
      );
    }

    ids.forEach((id) => values.set(id, missing));
    responses.forEach((pairs) => {
      for (const [id, value] of pairs) values.set(id, value);
    });
  };

  return async (ids: string[], signal: AbortSignal) => {
    const unique = [...new Set(ids)];
    const unseen = unique.filter((id) => !values.has(id) && !inFlight.has(id));

    if (unseen.length) {
      // Cleared either way, so a failed request is asked again on retry.
      const request = fetchValues(unseen, signal).finally(() =>
        unseen.forEach((id) => inFlight.delete(id)),
      );

      unseen.forEach((id) => inFlight.set(id, request));
    }

    await Promise.all(new Set(unique.flatMap((id) => inFlight.get(id) ?? [])));

    return values;
  };
};
